import { InterviewRecordingManager } from '@/services/recording'
import { logger } from '@/lib/logging'
import type { ScreenShareContext } from './screenShareTypes'
import { createPipComposition } from './pipComposition'
import { createMixedAudioStream } from './screenShareAudio'
import { closeAudioMixing, restoreCameraFeed, stopTracks } from './streamHelpers'

export async function startScreenSharingFlow(context: ScreenShareContext) {
  const { refs, showNotification } = context

  try {
    const screenStream = await navigator.mediaDevices.getDisplayMedia({
      video: { cursor: 'always' } as MediaTrackConstraints,
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    })

    refs.screenStreamRef.current = screenStream
    await showScreenStream(refs, screenStream)

    if (screenStream.getVideoTracks().length === 0) {
      logger.error('Screen stream has no video tracks')
      showNotification('Screen share failed: no video track')
      return
    }

    await new Promise((resolve) => setTimeout(resolve, 200))

    let audioStreamToUse = refs.mediaStreamRef.current
    const mixedStream = createMixedAudioStream(refs, screenStream, showNotification)
    if (mixedStream) audioStreamToUse = mixedStream

    let videoStreamToRecord = screenStream
    const pipStream = await createPipComposition(refs, screenStream)
    if (pipStream) videoStreamToRecord = pipStream

    if (!(await validateScreenStream(context, screenStream))) return

    await updateRecordingForScreenShare(context, videoStreamToRecord, audioStreamToUse)

    if (refs.serviceRef.current && audioStreamToUse) {
      refs.serviceRef.current.replaceInput(audioStreamToUse)
    }

    context.setIsScreenSharing(true)
    refs.isScreenSharingRef.current = true

    if (refs.serviceRef.current) {
      const track = screenStream.getVideoTracks()[0]
      refs.serviceRef.current.setScreenSharing(true)
      logger.info('AI set to screen sharing mode', {
        trackId: track.id,
        readyState: track.readyState,
      })
    }

    showNotification('Screen sharing active - AI viewing & listening')
    attachNativeStopHandler(context, screenStream)
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'NotAllowedError') {
      showNotification('Screen sharing cancelled')
      return
    }

    logger.error('Screen share error', { error: String(err) })
    showNotification('Failed to start screen sharing')
  }
}

async function showScreenStream(refs: ScreenShareContext['refs'], screenStream: MediaStream) {
  if (!refs.videoRef.current) return

  refs.videoRef.current.srcObject = screenStream
  try {
    await refs.videoRef.current.play()
  } catch (err) {
    logger.error('Error playing screen stream', { error: String(err) })
  }
}

async function validateScreenStream(context: ScreenShareContext, screenStream: MediaStream) {
  const { refs, showNotification } = context
  const tracks = screenStream.getVideoTracks()
  const isStreamActive = tracks.length > 0 && tracks[0].readyState === 'live'
  if (isStreamActive) return true

  logger.error('Screen stream not active, cannot start screen sharing', {
    trackCount: tracks.length,
    readyState: tracks[0]?.readyState,
  })
  showNotification('Screen share failed: stream not active')
  await closeAudioMixing(refs)
  stopTracks(refs.screenStreamRef.current)
  refs.screenStreamRef.current = null
  await restoreCameraFeed(refs)
  return false
}

async function updateRecordingForScreenShare(
  context: ScreenShareContext,
  videoStream: MediaStream,
  audioStream: MediaStream | null
) {
  const { refs, interviewId, apiBaseUrl, showNotification } = context

  if (refs.recordingManagerRef.current) {
    try {
      if (audioStream) refs.recordingManagerRef.current.updateMicrophoneStream(audioStream)
      await refs.recordingManagerRef.current.switchVideoSource(videoStream, true)
      logger.info('Recording switched to screen share (PiP)', {
        trackId: videoStream.getVideoTracks()[0].id,
        mixedAudio: !!refs.mixedAudioStreamRef.current,
      })
    } catch (err) {
      logger.error('Failed to update recording for screen share', { error: String(err) })
      showNotification('Recording may not include screen share')
    }
    return
  }

  if (audioStream && refs.serviceRef.current) {
    refs.recordingManagerRef.current = new InterviewRecordingManager({
      microphoneStream: audioStream,
      aiAudioService: refs.serviceRef.current,
      interviewId,
      apiBaseUrl,
    })
    refs.recordingManagerRef.current.startRecording(videoStream, true)
  }
}

function attachNativeStopHandler(context: ScreenShareContext, screenStream: MediaStream) {
  const screenVideoTrack = screenStream.getVideoTracks()[0]
  if (!screenVideoTrack) return

  screenVideoTrack.onended = null
  screenVideoTrack.onended = async () => {
    logger.info('Screen share ended by browser (native stop button clicked)')
    if (context.refs.isScreenSharingRef.current) {
      await context.stopScreenSharing()
    }
  }
}
